import { useState } from "react";
import { Download, Upload, User, Lock, Save, AlertCircle, CheckCircle, Home, Code, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const Account = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [codeExportLoading, setCodeExportLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const navigate = useNavigate();

  // Mock user data - replace with actual user context later
  const [userData, setUserData] = useState({
    name: "Christopher",
    email: "christopher@example.com",
    joinDate: "2024-01-15",
    lastBackup: null as string | null,
    lastCodeExport: null as string | null,
  });

  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });

  // Complete source code export - packages all app files
  const exportSourceCode = async () => {
    setCodeExportLoading(true);
    try {
      // Define the complete project structure with all files
      const sourceFiles = {
        'package.json': `{
  "name": "venturetrack",
  "private": true,
  "version": "1.0.0",
  "type": "module",
  "scripts": {
    "dev": "vite",
    "build": "tsc && vite build",
    "lint": "eslint . --ext ts,tsx --report-unused-disable-directives --max-warnings 0",
    "preview": "vite preview"
  },
  "dependencies": {
    "@radix-ui/react-dialog": "^1.0.5",
    "@radix-ui/react-select": "^2.0.0",
    "@radix-ui/react-slot": "^1.0.2",
    "@radix-ui/react-toast": "^1.1.5",
    "@radix-ui/react-tooltip": "^1.0.7",
    "@tanstack/react-query": "^4.36.1",
    "class-variance-authority": "^0.7.0",
    "clsx": "^2.0.0",
    "lucide-react": "^0.298.0",
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-router-dom": "^6.20.1",
    "recharts": "^2.8.0",
    "sonner": "^1.4.0",
    "tailwind-merge": "^2.2.0",
    "tailwindcss-animate": "^1.0.7"
  },
  "devDependencies": {
    "@types/react": "^18.2.43",
    "@types/react-dom": "^18.2.17",
    "@typescript-eslint/eslint-plugin": "^6.14.0",
    "@typescript-eslint/parser": "^6.14.0",
    "@vitejs/plugin-react": "^4.2.1",
    "autoprefixer": "^10.4.16",
    "eslint": "^8.55.0",
    "eslint-plugin-react-hooks": "^4.6.0",
    "eslint-plugin-react-refresh": "^0.4.5",
    "postcss": "^8.4.32",
    "tailwindcss": "^3.4.0",
    "typescript": "^5.2.2",
    "vite": "^5.0.8"
  }
}`,
        'README.md': `# VentureTrack - Business Analytics Dashboard

A comprehensive business management platform for entrepreneurs tracking multiple ventures with profit/expense analytics.

## Features

- **Multi-Venture Support**: Track multiple businesses with separate analytics
- **Interactive Charts**: Real-time profit vs expense visualization 
- **Data Backup & Export**: Complete export/import functionality
- **Customer/Supplier Management**: Contact databases with purchase history
- **Dark Mode**: Beautiful dark/light theme toggle
- **Simplified Mode**: Quick-entry interface for daily transactions

## Setup Instructions

1. Install dependencies:
\`\`\`bash
npm install
\`\`\`

2. Start development server:
\`\`\`bash
npm run dev
\`\`\`

3. Build for production:
\`\`\`bash
npm run build
\`\`\`

## Project Structure

- \`src/pages/\` - Main application pages
- \`src/components/\` - Reusable UI components  
- \`src/components/ui/\` - Base UI components (shadcn/ui)

Built with React + TypeScript + Vite + Tailwind CSS
`,
        'vite.config.ts': `import { defineConfig } from 'vite'
import react from '@vitejs/plugin-react'
import path from 'path'

export default defineConfig({
  plugins: [react()],
  resolve: {
    alias: {
      "@": path.resolve(__dirname, "./src"),
    },
  },
})
`,
        'tailwind.config.ts': `import type { Config } from "tailwindcss"

const config = {
  darkMode: ["class"],
  content: [
    './pages/**/*.{ts,tsx}',
    './components/**/*.{ts,tsx}',
    './app/**/*.{ts,tsx}',
    './src/**/*.{ts,tsx}',
  ],
  prefix: "",
  theme: {
    container: {
      center: true,
      padding: "2rem",
      screens: {
        "2xl": "1400px",
      },
    },
    extend: {
      colors: {
        border: "hsl(var(--border))",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        background: "hsl(var(--background))",
        foreground: "hsl(var(--foreground))",
        primary: {
          DEFAULT: "hsl(var(--primary))",
          foreground: "hsl(var(--primary-foreground))",
        },
        secondary: {
          DEFAULT: "hsl(var(--secondary))",
          foreground: "hsl(var(--secondary-foreground))",
        },
        destructive: {
          DEFAULT: "hsl(var(--destructive))",
          foreground: "hsl(var(--destructive-foreground))",
        },
        muted: {
          DEFAULT: "hsl(var(--muted))",
          foreground: "hsl(var(--muted-foreground))",
        },
        accent: {
          DEFAULT: "hsl(var(--accent))",
          foreground: "hsl(var(--accent-foreground))",
        },
        popover: {
          DEFAULT: "hsl(var(--popover))",
          foreground: "hsl(var(--popover-foreground))",
        },
        card: {
          DEFAULT: "hsl(var(--card))",
          foreground: "hsl(var(--card-foreground))",
        },
      },
      borderRadius: {
        lg: "var(--radius)",
        md: "calc(var(--radius) - 2px)",
        sm: "calc(var(--radius) - 4px)",
      },
      keyframes: {
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--radix-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--radix-accordion-content-height)" },
          to: { height: "0" },
        },
      },
      animation: {
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
      },
    },
  },
  plugins: [require("tailwindcss-animate")],
} satisfies Config

export default config
`,
        'tsconfig.json': `{
  "compilerOptions": {
    "target": "ES2020",
    "useDefineForClassFields": true,
    "lib": ["ES2020", "DOM", "DOM.Iterable"],
    "module": "ESNext",
    "skipLibCheck": true,
    "moduleResolution": "bundler",
    "allowImportingTsExtensions": true,
    "resolveJsonModule": true,
    "isolatedModules": true,
    "noEmit": true,
    "jsx": "react-jsx",
    "strict": true,
    "noUnusedLocals": true,
    "noUnusedParameters": true,
    "noFallthroughCasesInSwitch": true,
    "baseUrl": ".",
    "paths": {
      "@/*": ["./src/*"]
    }
  },
  "include": ["src"],
  "references": [{ "path": "./tsconfig.node.json" }]
}
`,
        'src/main.tsx': `import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { ThemeProvider } from "@/components/theme-provider"

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <App />
    </ThemeProvider>
  </React.StrictMode>,
)
`,
        'src/index.css': `@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    --background: 0 0% 100%;
    --foreground: 222.2 84% 4.9%;
    --card: 0 0% 100%;
    --card-foreground: 222.2 84% 4.9%;
    --popover: 0 0% 100%;
    --popover-foreground: 222.2 84% 4.9%;
    --primary: 221.2 83.2% 53.3%;
    --primary-foreground: 210 40% 98%;
    --secondary: 210 40% 96%;
    --secondary-foreground: 222.2 84% 4.9%;
    --muted: 210 40% 96%;
    --muted-foreground: 215.4 16.3% 46.9%;
    --accent: 210 40% 96%;
    --accent-foreground: 222.2 84% 4.9%;
    --destructive: 0 84.2% 60.2%;
    --destructive-foreground: 210 40% 98%;
    --border: 214.3 31.8% 91.4%;
    --input: 214.3 31.8% 91.4%;
    --ring: 221.2 83.2% 53.3%;
    --radius: 0.75rem;
    --chart-1: 12 76% 61%;
    --chart-2: 173 58% 39%;
    --chart-3: 197 37% 24%;
    --chart-4: 43 74% 66%;
    --chart-5: 27 87% 67%;
  }

  .dark {
    --background: 222.2 84% 4.9%;
    --foreground: 210 40% 98%;
    --card: 222.2 84% 4.9%;
    --card-foreground: 210 40% 98%;
    --popover: 222.2 84% 4.9%;
    --popover-foreground: 210 40% 98%;
    --primary: 217.2 91.2% 59.8%;
    --primary-foreground: 222.2 84% 4.9%;
    --secondary: 217.2 32.6% 17.5%;
    --secondary-foreground: 210 40% 98%;
    --muted: 217.2 32.6% 17.5%;
    --muted-foreground: 215 20.2% 65.1%;
    --accent: 217.2 32.6% 17.5%;
    --accent-foreground: 210 40% 98%;
    --destructive: 0 62.8% 30.6%;
    --destructive-foreground: 210 40% 98%;
    --border: 217.2 32.6% 17.5%;
    --input: 217.2 32.6% 17.5%;
    --ring: 224.3 76.3% 94.1%;
    --chart-1: 220 70% 50%;
    --chart-2: 160 60% 45%;
    --chart-3: 30 80% 55%;
    --chart-4: 280 65% 60%;
    --chart-5: 340 75% 55%;
  }
}

@layer base {
  * {
    @apply border-border;
  }
  body {
    @apply bg-background text-foreground;
  }
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
`
      };

      // Create the export bundle with all files and setup instructions
      const exportData = {
        version: "1.0.0",
        exportDate: new Date().toISOString(),
        projectName: "VentureTrack",
        description: "Complete VentureTrack source code export",
        setupInstructions: [
          "1. Extract all files to a new folder called 'venturetrack'",
          "2. Open terminal in the project folder",
          "3. Run: npm install",
          "4. Run: npm run dev",
          "5. Open http://localhost:5173 in your browser"
        ],
        sourceFiles: sourceFiles,
        additionalNotes: [
          "This export contains the complete VentureTrack application source code",
          "All components, pages, and configurations are included",
          "The app includes data backup/restore, dark mode, customer management, and analytics",
          "For support, refer to the README.md file included in the export"
        ]
      };

      // Create and download the complete source code export
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `venturetrack-source-code-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Update last export date
      setUserData(prev => ({ ...prev, lastCodeExport: new Date().toISOString() }));
      toast.success("Complete source code exported successfully!");
    } catch (error) {
      toast.error("Failed to export source code. Please try again.");
      console.error("Source code export error:", error);
    } finally {
      setCodeExportLoading(false);
    }
  };

  // Export all app data
  const exportData = async () => {
    setExportLoading(true);
    try {
      // Gather all data from localStorage or app state
      const appData = {
        version: "1.0",
        exportDate: new Date().toISOString(),
        userData: userData,
        transactions: JSON.parse(localStorage.getItem('ventures_transactions') || '[]'),
        customers: JSON.parse(localStorage.getItem('ventures_customers') || '[]'),
        suppliers: JSON.parse(localStorage.getItem('ventures_suppliers') || '[]'),
        products: JSON.parse(localStorage.getItem('ventures_products') || '[]'),
        profitGoals: JSON.parse(localStorage.getItem('ventures_profit_goals') || '{"WTD": 1000, "MTD": 3000, "YTD": 15000}'),
        ventures: JSON.parse(localStorage.getItem('ventures_list') || '[]'),
        settings: JSON.parse(localStorage.getItem('ventures_settings') || '{}'),
      };

      // Create and download file
      const dataStr = JSON.stringify(appData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `venturetrack-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Update last backup date
      setUserData(prev => ({ ...prev, lastBackup: new Date().toISOString() }));
      toast.success("Data exported successfully!");
    } catch (error) {
      toast.error("Failed to export data. Please try again.");
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  // Import data from backup file
  const importData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const fileText = await file.text();
      const importedData = JSON.parse(fileText);

      // Validate data structure
      if (!importedData.version || !importedData.exportDate) {
        throw new Error("Invalid backup file format");
      }

      // Restore data to localStorage
      if (importedData.transactions) localStorage.setItem('ventures_transactions', JSON.stringify(importedData.transactions));
      if (importedData.customers) localStorage.setItem('ventures_customers', JSON.stringify(importedData.customers));
      if (importedData.suppliers) localStorage.setItem('ventures_suppliers', JSON.stringify(importedData.suppliers));
      if (importedData.products) localStorage.setItem('ventures_products', JSON.stringify(importedData.products));
      if (importedData.profitGoals) localStorage.setItem('ventures_profit_goals', JSON.stringify(importedData.profitGoals));
      if (importedData.ventures) localStorage.setItem('ventures_list', JSON.stringify(importedData.ventures));
      if (importedData.settings) localStorage.setItem('ventures_settings', JSON.stringify(importedData.settings));

      toast.success(`Data restored from backup created on ${new Date(importedData.exportDate).toLocaleDateString()}`);
      // Offer to reload page to reflect changes
      if (confirm("Data imported successfully! Reload the page to see changes?")) {
        window.location.reload();
      }
    } catch (error) {
      toast.error("Failed to import data. Please check the file format.");
      console.error("Import error:", error);
    } finally {
      setImportLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle password change
  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwords.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    // Mock password change - replace with actual API call later
    toast.success("Password changed successfully!");
    setPasswords({ current: "", new: "", confirm: "" });
    setShowPasswordChange(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-background dark:to-card">
      {/* Home button */}
      <button
        onClick={() => navigate("/")}
        className="absolute left-4 top-4 bg-white dark:bg-card rounded-full p-2 shadow hover:bg-primary/10 text-primary transition z-20 border"
      >
        <Home size={22} />
      </button>

      <div className="max-w-2xl mx-auto pt-20 px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-primary/10 rounded-full p-3">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-primary">Account</h1>
              <p className="text-muted-foreground">Manage your profile and data backup</p>
            </div>
          </div>

          {/* Profile Section */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </h2>
            <div className="bg-muted/30 rounded-lg p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Name</label>
                  <div className="mt-1 text-lg font-medium">{userData.name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <div className="mt-1 text-lg font-medium">{userData.email}</div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Member Since</label>
                <div className="mt-1 text-lg font-medium">{new Date(userData.joinDate).toLocaleDateString()}</div>
              </div>
              <button className="mt-4 bg-muted hover:bg-muted/80 px-4 py-2 rounded-lg font-medium transition">
                Edit Profile
              </button>
            </div>
          </section>

          {/* Data Backup Section */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Download className="w-5 h-5" />
              Data Backup & Code Export
            </h2>
            <div className="bg-muted/30 rounded-lg p-6">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Export App Data */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Export App Data
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Download all your business data including transactions, customers, suppliers, and settings.
                  </p>
                  {userData.lastBackup && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <CheckCircle size={14} />
                      Last backup: {new Date(userData.lastBackup).toLocaleDateString()}
                    </div>
                  )}
                  <button
                    onClick={exportData}
                    disabled={exportLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {exportLoading ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <FileText size={18} />
                    )}
                    {exportLoading ? "Exporting..." : "Export Data"}
                  </button>
                </div>

                {/* Export Source Code */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Export Source Code
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Download the complete VentureTrack application source code. Perfect for sharing or local development.
                  </p>
                  {userData.lastCodeExport && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <CheckCircle size={14} />
                      Last export: {new Date(userData.lastCodeExport).toLocaleDateString()}
                    </div>
                  )}
                  <button
                    onClick={exportSourceCode}
                    disabled={codeExportLoading}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {codeExportLoading ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Code size={18} />
                    )}
                    {codeExportLoading ? "Exporting..." : "Export Source Code"}
                  </button>
                </div>
                {/* Import Data */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Import Data
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Restore your data from a previous backup file. This will replace your current data.
                  </p>
                  <div className="text-xs text-orange-600 flex items-center gap-1">
                    <AlertCircle size={14} />
                    Warning: This will overwrite your current data
                  </div>
                  <label className="w-full bg-muted hover:bg-muted/80 border-2 border-dashed border-muted-foreground/30 font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2 cursor-pointer">
                    {importLoading ? (
                      <div className="animate-spin w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full" />
                    ) : (
                      <Upload size={18} />
                    )}
                    {importLoading ? "Importing..." : "Choose Backup File"}
                    <input
                      type="file"
                      accept=".json"
                      onChange={importData}
                      className="hidden"
                      disabled={importLoading}
                    />
                  </label>
                </div>
              </div>

              {/* Export Instructions */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">📋 How to Use Source Code Export:</h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• <strong>Export Source Code</strong> - Downloads complete app code as JSON file</li>
                  <li>• <strong>Extract Files</strong> - Use the included setup instructions to recreate the project</li>
                  <li>• <strong>Email Safe</strong> - JSON file is perfect for sharing via email</li>
                  <li>• <strong>Local Development</strong> - Set up the complete project on any computer</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Security Section */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Security
            </h2>
            <div className="bg-muted/30 rounded-lg p-6">
              {!showPasswordChange ? (
                <div className="space-y-3">
                  <p className="text-muted-foreground">Keep your account secure with a strong password.</p>
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="bg-muted hover:bg-muted/80 px-4 py-2 rounded-lg font-medium transition"
                  >
                    Change Password
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Current Password</label>
                    <input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">New Password</label>
                    <input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
                    >
                      <Save size={16} />
                      Save Password
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordChange(false);
                        setPasswords({ current: "", new: "", confirm: "" });
                      }}
                      className="bg-muted hover:bg-muted/80 px-4 py-2 rounded-lg font-medium transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

          {/* Future Cloud Sync Placeholder */}
          <section>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Cloud Sync (Coming Soon)
            </h2>
            <div className="bg-muted/30 rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Automatic cloud backup and sync across all your devices will be available soon. This will provide:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Real-time sync across all devices
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Automatic daily backups
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Access your data from anywhere
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Collaborative features for teams
                </li>
              </ul>
              <button
                disabled
                className="mt-4 bg-muted text-muted-foreground px-4 py-2 rounded-lg font-medium cursor-not-allowed"
              >
                Enable Cloud Sync (Coming Soon)
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Account;